import React, { useState } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { TextField } from '@mui/material';
import MessageAlert from '../Common/MessageAlert';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

function ChangePassword({ onLoadingChange }) {
  const { mode } = useThemeMode();
  const [isEditing, setIsEditing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('**********');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleEdit = () => {
    setIsEditing(true);
    setCurrentPassword('');
    setNewPassword('');
    setMessage('');
    setSuccessMessage('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentPassword('**********');
    setNewPassword('');
    setMessage('');
    setSuccessMessage('');
  };

  const handleChangePassword = async () => {
    setMessage('');
    setSuccessMessage('');

    // Only mandatory field validation
    if (!currentPassword.trim()) {
      setMessage('Current password is required');
      setTimeout(() => {
        setMessage('');
      }, 5000);
      return;
    }
    
    if (!newPassword.trim()) {
      setMessage('New password is required');
      setTimeout(() => {
        setMessage('');
      }, 5000);
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage('New password must be at least 6 characters long');
      setTimeout(() => {
        setMessage('');
      }, 5000);
      return;
    }

    try {
      setLoading(true);
      onLoadingChange && onLoadingChange(true);
      
      // Using the project's API service as per user preferences
      const response = await dataAPI.changePassword({
        old_password: currentPassword,
        new_password: newPassword
      }, { showLoading: false }); // Disable global loading

      console.log('Password change response:', response);

      if (response && response.status === 'success') {
        setSuccessMessage('Password changed successfully!');
        setIsEditing(false);
        setCurrentPassword('**********');
        setNewPassword('');
        
        // Clear success message after 3 seconds (like TaskDetails)
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        setMessage(response?.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage(error?.response?.data?.message || error?.message || 'An error occurred while changing password');
    } finally {
      setLoading(false);
      onLoadingChange && onLoadingChange(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
    borderRadius: '8px',
    backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
    color: mode === 'dark' ? '#FFFFFF' : '#2c3e50',
    fontSize: '14px',
    fontFamily: 'Urbanist, Arial, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    marginBottom: '12px'
  };

  const disabledInputStyle = {
    ...inputStyle,
    backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f5f5f5',
    color: mode === 'dark' ? '#666666' : '#999999',
    cursor: 'not-allowed',
    fontSize: '18px',
    fontWeight: '600',
    letterSpacing: '2px'
  };

  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Urbanist, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginRight: '12px'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#26C3C0',
    color: '#ffffff'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: mode === 'dark' ? '#B0B0B0' : '#666666',
    border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`
  };

  const editButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: '#26C3C0',
    border: 'none',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '600'
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>

      {!isEditing && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{
            color: mode === 'dark' ? '#B0B0B0' : '#666666',
            fontSize: '14px',
          }}>
            Manage your account password
          </div>
          <button
            onClick={handleEdit}
            style={editButtonStyle}
          >
            Edit
          </button>
        </div>
      )}

      {/* Success Message Alert - like TaskDetails */}
      {successMessage && (
        <MessageAlert
          type="success"
          show={!!successMessage}
          message={successMessage}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '300px'
          }}
        />
      )}

      {/* Error Message Alert - like TaskDetails */}
      {message && (
        <MessageAlert
          type="error"
          show={!!message}
          message={message}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '300px'
          }}
        />
      )}

      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: isEditing ? '12px' : '16px' }}>
          <TextField
            type="password"
            fullWidth
            label={isEditing ? 'Current Password' : ''}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              readOnly: !isEditing,
              style: { height: '40px' }
            }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: mode === 'dark' ? '#444' : '#ddd',
                },
                '&:hover fieldset': {
                  borderColor: '#26C3C0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#26C3C0',
                },
                '&.Mui-error fieldset': {
                  borderColor: '#f44336',
                },
              },
              '& .MuiInputLabel-root': {
                color: mode === 'dark' ? '#999' : '#666',
                fontSize: '14px',
              },
              '& .MuiFormHelperText-root': {
                display: 'none', // Hide helper text to prevent height increase
              },
            }}
          />
        </div>

        {isEditing && (
          <div style={{ marginBottom: '12px' }}>
            <TextField
              type="password"
              fullWidth
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{ style: { height: '40px' } }}
              inputProps={{ autoComplete: 'new-password' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  display: 'none', // Hide helper text to prevent height increase
                },
              }}
            />
          </div>
        )}

        {isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
              <button
                onClick={handleChangePassword}
                disabled={loading}
                style={{
                  ...primaryButtonStyle,
                  padding: '8px 16px',
                  fontSize: '13px',
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
              <button
                onClick={handleCancel}
                style={{
                  ...secondaryButtonStyle,
                  padding: '8px 16px',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
            </div>
        )}
      </div>
    </div>
  );
}

export default ChangePassword;
